import usePlugins from './usePlugins';
import useGetAllRelationships from './useGetAllRelationships';
import useGetLinkedEntities from './useGetLinkedEntities';
import useRelevantRelationships from './useRelevantRelationships';
import useTargetEntityDomainsToType from './useTargetEntityDomainsToType';
import { ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA } from '../consts';

/**
 * Takes allRelationships and sourceEntityType and returns relationshipsMap
 * results examples:
 * relationshipsMap: {
    "b8d87b87-8682-473b-8ffc-debef4874bb2": [
      {
        "createdOn": "2020-02-24T09:46:38.957752+00:00",
        "domain": "autodesk-bim360-cost",
        "type": "pco",
        "id": "8ca43850-56ea-11ea-bea2-958005d674fb"
      },
      {
        "createdOn": "2020-02-20T09:18:55.746378+00:00",
        "domain": "autodesk-bim360-cost",
        "type": "pco",
        "id": "03dbf8d0-53c2-11ea-a23e-3d0724074dd0"
      }
    ]}
*/
const mapRelationshipsBySourceEntity = (allRelationships, sourceEntityType) => {
  const relationshipsMap = {};

  allRelationships.forEach(relationship => {
    let sourceEntityId;
    const linkedEntities = [];
    relationship.entities.forEach(entity => {
      if (
        entity.type === ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA[sourceEntityType].type &&
        entity.domain === ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA[sourceEntityType].domain
      ) {
        sourceEntityId = entity.id;
      } else {
        linkedEntities.push(entity);
      }
    });

    if (!relationshipsMap[sourceEntityId]) {
      relationshipsMap[sourceEntityId] = linkedEntities;
    } else {
      relationshipsMap[sourceEntityId] = [...relationshipsMap[sourceEntityId], ...linkedEntities];
    }
  });

  return relationshipsMap;
};

/**
 * @param {object} entitiesFromUseGetLinkedEntities
 * @returns {object} object of entity types as keys, value is object with entity id as key and value of the fetched entity
 * @example result example:
 * {
 *  PCO: {
 *    "c58d0b50-53c1-11ea-ae34-b7bf9aad3e10": {
 *      id: 'c58d0b50-53c1-11ea-ae34-b7bf9aad3e10',
 *      name: 'Hello world',
 *      entityType: 'PCO',
 *      .
 *      .
 *      .
 *    }
 *  }
 * }
 */
const toEntitiesByType = entitiesFromUseGetLinkedEntities => {
  return entitiesFromUseGetLinkedEntities.reduce((acc, entity) => {
    const { entityType } = entity.entity;
    if (!acc.hasOwnProperty(entityType)) acc[entityType] = {};
    acc[entityType][entity.entity.id] = entity.entity;
    return acc;
  }, {});
};
/**
 * @param {object} relationshipsMap
 * @param {object} entitiesByType
 * @param {object} targetEntityDomainsToType
 * @returns {object} object with source entities id as keys (e.g RFI), the value is an array of fetched linked entities (e.g - pco).
 * @example result example:
 * {
 *  "20654c8c-d06b-41b7-a8d6-2f8b8e583104": [
 *    {
 *      "c58d0b50-53c1-11ea-ae34-b7bf9aad3e10": {
 *      id: 'c58d0b50-53c1-11ea-ae34-b7bf9aad3e10',
 *      name: 'Hello world',
 *      entityType: 'PCO',
 *      .
 *      .
 *      .
 *    }
 *  ]
 * }
 */
const toLinkedEntitiesMap = (
  relationshipsMap = {},
  entitiesByType = [],
  targetEntityDomainsToType
) => {
  const linkedEntitiesMap = {};
  Object.keys(relationshipsMap).forEach(sourceEntityId => {
    linkedEntitiesMap[sourceEntityId] = relationshipsMap[sourceEntityId]
      .map(entity => {
        const entityType = targetEntityDomainsToType[entity.domain][entity.type];
        return entitiesByType[entityType] && entitiesByType[entityType][entity.id];
      })
      .filter(entity => entity);
  });
  return linkedEntitiesMap;
};

/**
 *
 * @param {object} obj
 * @param {string[]} obj.ids array of entities ids
 * @param {string} obj.sourceEntityType the type of entities described in ids
 * @param {string[]} obj.plugins array of plugins (e.g ['PCO','ISSUES'])
 * @param {boolean} obj.autoLoadMore when set true fetch all entities per plugin
 * @returns {object} results
 * @returns {object} results.linkedEntitiesMap
 * @returns {boolean} results.isLoadingRelationships indicates if fetching is in progress
 * @returns {function} results.addRelationships callback to add a relationship
 * @returns {function} results.removeRelationship callback to remove a relationship
 * @returns {function | undefined} results.onLoadMore callback to load more entities
 * @returns {boolean} results.error
 *
 * @example
 * linkedEntitiesMap: {
 *  '20654c8c-d06b-41b7-a8d6-2f8b8e583104': [
 *    {
 *      id: '58d0b50-53c1-11ea-ae34-b7bf9aad3e10',
 *      containerId: '58726410-a300-11e9-903b-bdd162561700',
 *      name: 'Hello',
 *      description: 'World',
 *      'entityType': 'PCO',
 *      .
 *      .
 *      .
 *    }
 *  ]
 * }
 */
const useGetBulkLinkedEntities = ({ ids, sourceEntityType, plugins = [], autoLoadMore }) => {
  const { typeToPlugin, entityTypes } = usePlugins(plugins);
  const targetEntityDomainsToType = useTargetEntityDomainsToType(entityTypes);

  // request /v2/containers/{containerId}/relationships:search
  const {
    isLoading: isLoadingRelationships,
    error: errorLoadingRelationships,
    relationships: allRelationships,
    addRelationships,
    removeRelationship
  } = useGetAllRelationships(sourceEntityType, ids);

  /**
   * Format data we get from /v2/containers/{containerId}/relationships:search
   * Into data format useGetLinkedEntities expects.
   */
  const relationships = useRelevantRelationships(sourceEntityType, entityTypes, allRelationships);

  const relationshipsMap = mapRelationshipsBySourceEntity(
    relationships.map(({ relationship }) => relationship),
    sourceEntityType
  );

  /**
   * For every entityType (eg - 'pco') get all entities from /v2/containers/{containerId}/relationships:search
   */
  const {
    entities,
    error: errorLoadingEntities,
    onLoadMore,
    isLoading: isLoadingEntities
  } = useGetLinkedEntities({
    entityTypes,
    typeToPlugin,
    relationships,
    pageLimit: 100,
    autoLoadMore
  });

  const entitiesByType = toEntitiesByType(entities);
  const linkedEntitiesMap = toLinkedEntitiesMap(
    relationshipsMap,
    entitiesByType,
    targetEntityDomainsToType
  );

  return {
    linkedEntitiesMap,
    isLoadingRelationships,
    isLoadingEntities,
    addRelationships,
    removeRelationship,
    onLoadMore,
    error: errorLoadingRelationships || errorLoadingEntities
  };
};

export default useGetBulkLinkedEntities;
